/**
 * Monte Carlo simulator for prop-firm challenge pass odds.
 *
 * Trader places one trade per session. Each trade either wins R times the
 * risk amount (probability = winRate) or loses one R (probability = 1 - winRate).
 * The challenge is "passed" if account equity exceeds (1 + profitTargetPct/100)
 * of starting balance before either:
 *   - cumulative drawdown breaches maxDrawdownPct, OR
 *   - tradingDays run out.
 *
 * This is a simplification (no overnight gaps, no slippage, single risk-per-trade).
 * Good enough for an intuition-builder, not a substitute for live testing.
 */

export type PassOddsInput = {
  startingBalance: number; // e.g. 100000
  winRatePct: number; // 30-70 typical
  riskRewardRatio: number; // e.g. 2 means 2R win, -1R loss
  riskPerTradePct: number; // e.g. 1 means risk 1% per trade
  profitTargetPct: number; // e.g. 10
  maxDrawdownPct: number; // e.g. 10
  tradingDays: number; // e.g. 30
  sims: number; // default 10_000
};

export type PassOddsOutput = {
  passRatePct: number; // 0-100
  averageTradesToPass: number | null;
  averageTradesToBust: number | null;
  median: number;
};

function mulberry32(seed: number) {
  let t = seed >>> 0;
  return function () {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

// Clamp inputs to defensive ranges so a malicious DOM-edited request can't
// freeze the browser tab with a 1M-day, 1M-sim run.
const MAX_TRADING_DAYS = 1000;
const MAX_SIMS = 50_000;

export function simulatePassOdds(input: PassOddsInput, seed = 42): PassOddsOutput {
  const startingBalance = Math.max(100, Math.min(input.startingBalance, 10_000_000));
  const winRatePct = Math.max(1, Math.min(input.winRatePct, 99));
  const riskRewardRatio = Math.max(0.1, Math.min(input.riskRewardRatio, 20));
  const riskPerTradePct = Math.max(0.1, Math.min(input.riskPerTradePct, 10));
  const profitTargetPct = Math.max(1, Math.min(input.profitTargetPct, 100));
  const maxDrawdownPct = Math.max(1, Math.min(input.maxDrawdownPct, 100));
  const tradingDays = Math.max(1, Math.min(input.tradingDays, MAX_TRADING_DAYS));
  const sims = Math.max(100, Math.min(input.sims, MAX_SIMS));

  const rand = mulberry32(seed);
  const winProb = winRatePct / 100;
  const riskUsd = startingBalance * (riskPerTradePct / 100);
  const winUsd = riskUsd * riskRewardRatio;
  const targetUsd = startingBalance * (1 + profitTargetPct / 100);
  const ddLimitUsd = startingBalance * (1 - maxDrawdownPct / 100);

  let passes = 0;
  let passTradeSum = 0;
  let bustTradeSum = 0;
  let bustCount = 0;
  const finalBalances: number[] = [];

  for (let s = 0; s < sims; s++) {
    let balance = startingBalance;
    let peakBalance = startingBalance; // for trailing-DD interpretation
    let result: "pass" | "bust" | "timeout" = "timeout";
    let tradesTaken = 0;

    for (let d = 0; d < tradingDays; d++) {
      const isWin = rand() < winProb;
      balance += isWin ? winUsd : -riskUsd;
      peakBalance = Math.max(peakBalance, balance);
      tradesTaken++;

      // Static DD (not trailing) — firms vary; we model the simpler case.
      if (balance <= ddLimitUsd) {
        result = "bust";
        bustTradeSum += tradesTaken;
        bustCount++;
        break;
      }
      if (balance >= targetUsd) {
        result = "pass";
        passes++;
        passTradeSum += tradesTaken;
        break;
      }
    }

    finalBalances.push(balance);
    if (result === "timeout") {
      // Treat timeouts that finished green-of-start as "didn't pass" — neither pass nor bust.
    }
  }

  finalBalances.sort((a, b) => a - b);
  const median = finalBalances[Math.floor(finalBalances.length / 2)] ?? startingBalance;

  return {
    passRatePct: Math.round((passes / sims) * 1000) / 10,
    averageTradesToPass: passes > 0 ? Math.round(passTradeSum / passes) : null,
    averageTradesToBust: bustCount > 0 ? Math.round(bustTradeSum / bustCount) : null,
    median: Math.round(median),
  };
}
